import { useEffect, useState, useRef } from "react";

import { ENQUIRY_TYPE } from "@/consts";
import {
  EcommerceItem,
  EcommerceParams,
  gaGenerateLead,
} from "@/utils/tracking";

import { useCYOQuery } from "../api/useCreateYourOwnQuery";
import CreateYourOwnModal from "./CreateYourOwnModal";
import CreateYourOwnPersonalForm, {
  CreateYourOwnPersonalFormData,
} from "./CreateYourOwnPersonalForm";
import CreateYourOwnTravelForm, {
  CreateYourOwnTravelFormData,
} from "./CreateYourOwnTravelForm";

enum Step {
  TRAVEL = "travel",
  PERSONAL = "personal",
}

interface CreateYourOwnContainerProps {
  venueName?: string;
  isEu?: boolean;
  hotels?: Record<string, { name: string }>;
  courseNames?: string[];
}

export default function CreateYourOwnContainer(
  props: CreateYourOwnContainerProps,
) {
  const { courseNames, hotels, venueName, isEu } = props;

  const [step, setStep] = useState<"travel" | "personal">("travel");
  const [travelDetails, setTravelDetails] =
    useState<CreateYourOwnTravelFormData | null>(null);
  const [personalDetails, setPersonalDetails] =
    useState<CreateYourOwnPersonalFormData | null>(null);
  const [modalState, setModalState] = useState<"success" | "error" | null>(
    null,
  );

  const [enquiryId, setEnquiryId] = useState<number | null>(null);
  const trackedEnquiryIds = useRef(new Set<number>());

  useEffect(() => {
    if (!travelDetails || enquiryId === null) return;
    if (trackedEnquiryIds.current.has(enquiryId)) return;

    const item: EcommerceItem = {
      affiliation: "CYO",
      itemId: `CYO-${venueName?.replace(/\s+/g, "-") || "custom"}`,
      itemName: venueName || "Create Your Own",
      itemVariant: `${travelDetails.nights} nights, ${travelDetails.rounds} rounds`,
      quantity: parseInt(travelDetails.group_size, 10) || 1,
    };

    const params: EcommerceParams = {
      items: [item],
    };

    gaGenerateLead(params);
    trackedEnquiryIds.current.add(enquiryId);
  }, [enquiryId, travelDetails, venueName]);

  const { isSubmittingCYO, submitCYO } = useCYOQuery({
    onSuccess: () => setModalState("success"),
    onError: () => setModalState("error"),
  });

  const handleTravelContinue = (data: CreateYourOwnTravelFormData) => {
    setTravelDetails(data);
    setStep(Step.PERSONAL);
  };

  const handlePersonalSubmit = async (data: CreateYourOwnPersonalFormData) => {
    if (!travelDetails) return;

    setPersonalDetails(data);

    const payload = {
      date: travelDetails.arrival_date,
      group_size: parseInt(travelDetails.group_size, 10),
      email: data.email,
      title: data.title,
      first_name: data.first_name,
      last_name: data.last_name,
      dialing_code: data.dialing_code,
      mobile: data.mobile,
      package_id: null,
      receive_offers: data.receive_offers,
      destination: null,
      nights: parseInt(travelDetails.nights, 10),
      rounds: parseInt(travelDetails.rounds, 10),
      include_flight_options: travelDetails.include_flight_options || false,
      flight_options: travelDetails.flight_options || null,
      additional_information: "",
      type: ENQUIRY_TYPE.VENUE,
      courses: travelDetails.courses || null,
      hotels,
    };

    const response = await submitCYO(payload);
    setEnquiryId(response.data?.id);
  };

  return (
    <>
      {step === Step.TRAVEL && (
        <CreateYourOwnTravelForm
          onContinue={handleTravelContinue}
          courseNames={courseNames}
          isEu={isEu}
          initialData={travelDetails || undefined}
        />
      )}

      {step === Step.PERSONAL && (
        <CreateYourOwnPersonalForm
          initialData={personalDetails || undefined}
          onSubmit={handlePersonalSubmit}
          isSubmitting={isSubmittingCYO}
          summaryInfo={
            travelDetails
              ? {
                  date: travelDetails.arrival_date,
                  nights: travelDetails.nights,
                  rounds: travelDetails.rounds,
                  venueName,
                }
              : undefined
          }
          onBack={() => setStep(Step.TRAVEL)}
        />
      )}

      <CreateYourOwnModal
        key={modalState}
        isOpen={!!modalState}
        modalState={modalState}
        travelDetails={travelDetails}
        personalDetails={personalDetails}
        venueName={venueName}
        setModalState={setModalState}
        setTravelDetails={setTravelDetails}
        setPersonalDetails={setPersonalDetails}
        setStep={setStep}
      />
    </>
  );
}
